import store from '@/store'
import { createAuth0Client } from '@auth0/auth0-spa-js'
import { settings } from '@/utils/settings'
import Bugsnag from '@bugsnag/js'
import { v4 as uuidv4 } from 'uuid'
import { FTWebSocket } from '@/utils/FTWebSocket'

/**
 * Mutation types of the store
 */
const mutationTypes = {
  LOGOUT: 'LOGOUT',
  CLEAR_INFO: 'CLEAR_INFO',
  SET_INFO: 'SET_INFO',
  REFRESH_TOKEN: 'REFRESH_TOKEN',
  UPDATE_EMAIL: 'UPDATE_EMAIL',
  UPDATE_ACCOUNT_DETAILS: 'UPDATE_ACCOUNT_DETAILS'
}

/**
 * Default state
 */
const defState = {
  __auth0Client: undefined, // internal use only!
  accounts: [],
  accessToken: '',
  contact: {},
  email: '',
  emailVerified: false,
  multiSupplier: false,
  picture: '',
  userId: ''
}

/**
 * Initial state
 */
const state = { ...defState }

/**
 * Getters
 */
const getters = {
  isAuthenticated: (state) => {
    return !!state.accessToken
  },

  getAuth0Client: async (state) => {
    if (state.__auth0Client) {
      return state.__auth0Client
    }

    if (!store.getters['tenant/auth0ClientId']) {
      await store.dispatch('tenant/fetchTenantSettings')
    }
    const clientId = store.getters['tenant/auth0ClientId']
    if (clientId) {
      state.__auth0Client = await createAuth0Client({
        domain: settings.AUTH0_DOMAIN,
        clientId,
        cacheLocation: 'localstorage',
        useRefreshTokens: true,
        authorizationParams: {
          audience: settings.AUTH0_AUDIENCE,
          scope: 'openid profile email',
          redirect_uri: window.location.origin
        }
      })
    }
    return state.__auth0Client
  }
}

/**
 * Actions
 */
const actions = {
  async checkSession({ commit, dispatch }) {
    const auth0 = await getters.getAuth0Client(state)
    try {
      const token = await auth0.getTokenSilently()
      if (await auth0.isAuthenticated()) {
        const user = await auth0.getUser()
        Bugsnag.setUser(user.sub)
        commit(mutationTypes.SET_INFO, { user, token })
        return token
      } else {
        Bugsnag.setUser(uuidv4())
        commit(mutationTypes.CLEAR_INFO, {})
        dispatch('preferences/clearSelection', {}, { root: true })
      }
    } catch (e) {
      Bugsnag.setUser(uuidv4())
      commit(mutationTypes.CLEAR_INFO, {})
      dispatch('preferences/clearSelection', {}, { root: true })
    }
  },

  async login({ dispatch }) {
    dispatch('preferences/clearSelection', null, { root: true })
    const auth0 = await getters.getAuth0Client(state)
    await auth0.loginWithRedirect({
      authorizationParams: {
        connection: store.getters['tenant/auth0Realm'],
        redirect_uri: `${window.location.origin}/account/login`
      }
    })

    if (window.gtag) {
      if (await auth0.isAuthenticated()) {
        window.gtag('event', 'loginSuccessful')
      } else {
        window.gtag('event', 'loginUnsuccessful')
      }
    }
  },

  async logout({ commit, dispatch }) {
    dispatch('preferences/clearSelection', null, { root: true })
    commit(mutationTypes.LOGOUT)
    if (window.gtag) window.gtag('event', 'logoutSuccessful')
    Bugsnag.setUser(uuidv4())
    const auth0 = await getters.getAuth0Client(state)
    await auth0.logout({ logoutParams: { returnTo: window.location.origin } })
  },

  async getTokenSilently({ commit }) {
    const auth0 = await getters.getAuth0Client(state)
    const accessToken = await auth0.getTokenSilently()
    commit(mutationTypes.REFRESH_TOKEN, accessToken)
  },

  async setToken({ commit }) {
    const auth0 = await getters.getAuth0Client(state)
    await auth0.handleRedirectCallback()

    if (await auth0.isAuthenticated()) {
      const token = await auth0.getTokenSilently()
      const user = await auth0.getUser()
      if (window.gtag) {
        window.gtag('config', { user_id: user.sub })
        window.gtag('event', 'loginSuccessfulAuth')
      }
      Bugsnag.setUser(user.sub)
      commit(mutationTypes.SET_INFO, { user, token })
    } else {
      if (window.gtag) {
        window.gtag('event', 'loginUnsuccessfulAuth')
      }
    }
  },

  updateSupplierEmail({ commit }, payload) {
    commit(mutationTypes.UPDATE_EMAIL, payload)
  },

  updateAccountDetails({ commit }, payload) {
    commit(mutationTypes.UPDATE_ACCOUNT_DETAILS, payload)
  }
}

/**
 * Mutations
 */
const mutations = {
  [mutationTypes.LOGOUT](state) {
    FTWebSocket.disconnect()
    Object.assign(state, defState)
  },

  [mutationTypes.CLEAR_INFO](state) {
    Object.assign(state, defState)
  },

  [mutationTypes.SET_INFO](state, payload) {
    const metadata = payload.user['https://flewidtech.com/app_metadata']
    state.accounts = metadata.accounts
    state.accessToken = payload.token
    state.contact = metadata.contact
    state.email = payload.user.email
    state.emailVerified = payload.user.email_verified
    state.multiSupplier = metadata.accounts.length > 1
    state.picture = payload.user.picture
    state.userId = payload.user.sub
  },

  [mutationTypes.REFRESH_TOKEN](state, accessToken) {
    state.accessToken = accessToken
    FTWebSocket.connect() // If the websocket was disconnected due to user inactivity, if we've now
    // been able to obtain a new refresh token then we can (re)connect
  },

  [mutationTypes.UPDATE_EMAIL](state, payload) {
    state.email = payload
  },

  [mutationTypes.UPDATE_ACCOUNT_DETAILS](state, payload) {
    state.contact = { ...payload }
  }
}

export default {
  namespaced: true,
  actions,
  getters,
  mutations,
  state
}
